import { useEffect, useRef, useState } from 'react';
import { checkDiscountPct } from '@makila/pos-core';
import { X } from 'lucide-react';
import type { IdbTerminalStore } from '../lib/idb-store';
import type { Operator } from '../screens/Lock';
import { ManagerApproval } from './ManagerApproval';

const QUICK = ['5', '10', '15', '20'];

/**
 * Desconto (%) numa linha ou no total. Se ainda não houver gerente a autorizar
 * os descontos desta venda, pede-o primeiro; a autorização vale para a venda.
 */
export function DiscountDialog(p: {
  title: string; current: string; store: IdbTerminalStore; operator: Operator; approvedBy: Operator | null;
  onApply: (pct: string, approver: Operator) => void; onClose: () => void;
}) {
  const dlg = useRef<HTMLDialogElement>(null);
  const [approver, setApprover] = useState<Operator | null>(p.approvedBy);
  const [v, setV] = useState(p.current === '0' ? '' : p.current.replace('.', ','));
  const [error, setError] = useState<string | null>(null);
  useEffect(() => { dlg.current?.showModal(); }, []);

  const apply = (raw: string) => {
    try {
      const pct = checkDiscountPct((raw || '0').replace(',', '.'));
      if (approver) p.onApply(pct, approver);
    } catch (e) { setError((e as Error).message); }
  };

  return (
    <dialog ref={dlg} onCancel={(e) => { e.preventDefault(); p.onClose(); }} className="m-auto w-[min(480px,96vw)] rounded-2xl bg-[var(--surface)] p-0 text-[var(--text)]">
      <div className="flex items-center justify-between border-b border-base px-5 py-3">
        <h2 className="text-xl font-bold">{p.title}</h2>
        <button aria-label="Fechar" onClick={p.onClose} className="grid h-12 w-12 place-items-center rounded-lg"><X /></button>
      </div>
      <div className="p-5">
        {!approver && <ManagerApproval store={p.store} operator={p.operator} why="Os descontos precisam da autorização de um gerente (vale para esta venda)." onApproved={setApprover} />}
        {approver && (
          <form onSubmit={(e) => { e.preventDefault(); apply(v); }}>
            {approver.id !== p.operator.id && <p className="mb-3 text-sm text-muted">Autorizado por {approver.name}.</p>}
            <div className="flex items-center gap-2">
              <input autoFocus inputMode="decimal" aria-label="Desconto em percentagem" placeholder="0" value={v} onChange={(e) => { setV(e.target.value); setError(null); }}
                className="num h-14 min-w-0 flex-1 rounded-xl border border-base bg-surface-2 px-4 text-right text-3xl" />
              <span className="text-3xl font-bold">%</span>
            </div>
            <div className="mt-3 grid grid-cols-5 gap-2">
              {QUICK.map((q) => <button type="button" key={q} onClick={() => apply(q)} className="h-12 rounded-xl border border-base font-semibold">{q}%</button>)}
              <button type="button" onClick={() => apply('0')} className="h-12 rounded-xl border border-base text-sm font-semibold">Sem desc.</button>
            </div>
            {error && <p role="alert" className="mt-3 rounded-lg bg-brand-50 p-3 text-sm text-brand-800">{error}</p>}
            <button className="mt-4 h-14 w-full rounded-xl bg-brand-700 text-lg font-bold text-white">Aplicar</button>
          </form>
        )}
      </div>
    </dialog>
  );
}
